import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import ModalCloseButton from "@/components/base/ModalCloseButton";
import {
  MODAL_FOOTER_CANCEL_BUTTON_CLASS,
  MODAL_FOOTER_PRIMARY_BUTTON_CLASS,
  MODAL_FOOTER_PRIMARY_BUTTON_STYLE,
} from "@/components/base/modalFooterButtonStyles";
import { docCategoryApi, toErrorMessage, type DocTaxonomyItem } from "@/services/docTaxonomy";
import type { DocCategoryAccessSettings } from "@/services/docTaxonomy";
import ConfirmDialog from "../recommendations/components/ConfirmDialog";
import AdminLayout from "@/components/layout/AdminLayout";
import { AdminSearchForm } from "@/components/admin/AdminTableToolbar";

type DocumentCategory = DocTaxonomyItem;

interface CategorySettingStatusProps {
  enabled: boolean;
  label: string;
  enabledLabel: string;
  disabledLabel: string;
  updating?: boolean;
  onToggle: () => void;
}

export function CategorySettingStatus({
  enabled,
  label,
  enabledLabel,
  disabledLabel,
  updating = false,
  onToggle,
}: CategorySettingStatusProps) {
  const statusLabel = enabled ? enabledLabel : disabledLabel;

  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={label}
      aria-busy={updating}
      title={statusLabel}
      disabled={updating}
      onClick={onToggle}
      className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#830051]/30 ${
        updating ? "cursor-wait opacity-60" : "cursor-pointer"
      } ${enabled ? "bg-[#830051]" : "bg-gray-300"}`}
    >
      <span
        className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
          enabled ? "translate-x-4" : "translate-x-0"
        }`}
      />
    </button>
  );
}

interface CategoryModalProps {
  mode: "add" | "edit";
  category?: DocumentCategory;
  onSave: (name: string, prompt: string, settings: DocCategoryAccessSettings) => void;
  onCancel: () => void;
}

export function CategoryModal({ mode, category, onSave, onCancel }: CategoryModalProps) {
  const { t } = useTranslation();
  const isBuiltIn = category?.is_builtin ?? false;
  const isReadOnly = mode === "edit" && category ? !category.is_editable : false;
  const canEditName = !isReadOnly;
  const canEditPrompt = !isReadOnly;

  const [name, setName] = useState(category?.name ?? "");
  const [prompt, setPrompt] = useState(category?.prompt ?? "");
  const [isVisibleToAllUsers, setIsVisibleToAllUsers] = useState(
    category?.is_visible_to_all_users ?? false,
  );
  const [isDefaultSelected, setIsDefaultSelected] = useState(
    category?.is_default_selected ?? false,
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onSave(name.trim(), prompt.trim(), {
        isVisibleToAllUsers,
        isDefaultSelected,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-xl shadow-sm w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-50">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              {mode === "add"
                ? t("docCategories.modal.newTitle")
                : t("docCategories.modal.editTitle")}
            </h3>
            {isBuiltIn && (
              <p className="text-[11px] text-amber-600 mt-0.5">
                {t("docCategories.modal.builtInNotice")}
              </p>
            )}
          </div>
          <ModalCloseButton ariaLabel={t("common.close")} onClick={onCancel} />
        </div>

        <div className="px-6 py-5 flex flex-col gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1.5">
              {t("docCategories.categoryName")} <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => canEditName && setName(e.target.value)}
              placeholder={t("docCategories.placeholder.name")}
              disabled={!canEditName}
              className="w-full text-sm border border-gray-200 rounded-lg px-3.5 py-2.5 outline-none focus:border-gray-400 transition-colors disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
              autoFocus={canEditName}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1.5">
              {t("docCategories.classificationStandard")}
            </label>
            <textarea
              value={prompt}
              onChange={(e) => canEditPrompt && setPrompt(e.target.value)}
              placeholder={t("docCategories.placeholder.prompt")}
              rows={4}
              disabled={!canEditPrompt}
              className="w-full text-sm border border-gray-200 rounded-lg px-3.5 py-2.5 outline-none focus:border-gray-400 bg-white resize-none transition-colors disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
            />
          </div>
          <div className="divide-y divide-gray-100 rounded-lg border border-gray-100">
            <div className="flex items-center justify-between gap-4 px-3.5 py-2.5">
              <span className="text-sm font-medium text-gray-800">
                {t("docCategories.visibleToAllUsers")}
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={isVisibleToAllUsers}
                aria-label={t("docCategories.visibleToAllUsers")}
                disabled={isReadOnly}
                onClick={() => setIsVisibleToAllUsers((value) => !value)}
                className={`relative h-5 w-9 shrink-0 rounded-full transition-colors duration-200 focus:outline-none ${
                  isReadOnly ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                } ${isVisibleToAllUsers ? "bg-[#830051]" : "bg-gray-300"}`}
              >
                <span
                  className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                    isVisibleToAllUsers ? "translate-x-4" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
            <div className="flex items-center justify-between gap-4 px-3.5 py-2.5">
              <span className="text-sm font-medium text-gray-800">
                {t("docCategories.defaultSelected")}
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={isDefaultSelected}
                aria-label={t("docCategories.defaultSelected")}
                disabled={isReadOnly}
                onClick={() => setIsDefaultSelected((value) => !value)}
                className={`relative h-5 w-9 shrink-0 rounded-full transition-colors duration-200 focus:outline-none ${
                  isReadOnly ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                } ${isDefaultSelected ? "bg-[#830051]" : "bg-gray-300"}`}
              >
                <span
                  className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                    isDefaultSelected ? "translate-x-4" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 sticky bottom-0 bg-white">
          {isReadOnly ? (
            <button
              onClick={onCancel}
              className="text-xs px-4 py-1.5 rounded-lg text-white cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90"
              style={{ backgroundColor: "#830051" }}
            >
              {t("common.close")}
            </button>
          ) : (
            <>
              <button onClick={onCancel} className={MODAL_FOOTER_CANCEL_BUTTON_CLASS}>
                {t("common.cancel")}
              </button>
              <button
                onClick={handleSave}
                disabled={!name.trim() || saving}
                className={`${MODAL_FOOTER_PRIMARY_BUTTON_CLASS} disabled:opacity-40 disabled:cursor-not-allowed`}
                style={MODAL_FOOTER_PRIMARY_BUTTON_STYLE}
              >
                {saving ? (
                  <span className="flex items-center gap-1">
                    <i className="ri-loader-4-line animate-spin"></i>
                    {t("common.saving")}
                  </span>
                ) : (
                  t("common.save")
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DocumentCategoriesPage() {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<DocumentCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const [modalMode, setModalMode] = useState<"add" | "edit" | null>(null);
  const [editTarget, setEditTarget] = useState<DocumentCategory | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<DocumentCategory | null>(null);
  const [updatingSettings, setUpdatingSettings] = useState<Record<string, boolean>>({});
  const updatingSettingKeys = useRef(new Set<string>());

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 后端已排好序（Custom 创建时间倒序在前，System 固定序在后），此处不再二次排序
      setCategories(await docCategoryApi.list());
    } catch (err) {
      setError(toErrorMessage(err, t("common.error")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleSave = async (name: string, prompt: string, settings: DocCategoryAccessSettings) => {
    try {
      if (modalMode === "add") {
        await docCategoryApi.create(name, prompt || null, settings);
      } else if (modalMode === "edit" && editTarget) {
        await docCategoryApi.update(editTarget.id, name, prompt || null, settings);
      }
    } catch (err) {
      // 重名、System 行保护等服务端校验错误在此展示，弹窗保持打开供用户修改
      setError(toErrorMessage(err, t("common.error")));
      return;
    }
    setModalMode(null);
    setEditTarget(undefined);
    await fetchCategories();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await docCategoryApi.remove(deleteTarget.id);
    } catch (err) {
      setError(toErrorMessage(err, t("common.error")));
      return;
    }
    setDeleteTarget(null);
    await fetchCategories();
  };

  const handleToggleSetting = async (
    category: DocumentCategory,
    setting: "is_visible_to_all_users" | "is_default_selected",
  ) => {
    const requestKey = `${category.id}:${setting}`;
    if (updatingSettingKeys.current.has(requestKey)) return;

    const previousValue = Boolean(category[setting]);
    const nextValue = !previousValue;
    updatingSettingKeys.current.add(requestKey);
    setUpdatingSettings((current) => ({ ...current, [requestKey]: true }));
    setError(null);
    setCategories((current) =>
      current.map((item) => (item.id === category.id ? { ...item, [setting]: nextValue } : item)),
    );

    try {
      if (setting === "is_visible_to_all_users") {
        await docCategoryApi.setVisibleToAllUsers(category.id, nextValue);
      } else {
        await docCategoryApi.setDefaultSelected(category.id, nextValue);
      }
    } catch (err) {
      setCategories((current) =>
        current.map((item) =>
          item.id === category.id ? { ...item, [setting]: previousValue } : item,
        ),
      );
      setError(toErrorMessage(err, t("common.error")));
    } finally {
      updatingSettingKeys.current.delete(requestKey);
      setUpdatingSettings((current) => {
        const next = { ...current };
        delete next[requestKey];
        return next;
      });
    }
  };

  const filtered = categories.filter((cat) => {
    const matchSearch =
      !searchQuery.trim() ||
      cat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cat.stable_key.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (cat.prompt?.toLowerCase() ?? "").includes(searchQuery.toLowerCase());
    return matchSearch;
  });

  return (
    <AdminLayout>
      <div>
        {/* Header */}
        <div className="mb-5 flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-1.5">
              {t("docCategories.title")}
              <span className="relative group cursor-help">
                <i className="ri-question-line text-gray-400 text-sm w-4 h-4 flex items-center justify-center"></i>
                <span className="absolute top-full mt-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block w-64 text-[11px] text-gray-600 bg-white border border-gray-200 rounded-md shadow-sm px-2.5 py-1.5 z-50 leading-relaxed">
                  {t("docCategories.tooltip")}
                </span>
              </span>
            </h2>
          </div>
          <button
            onClick={() => {
              setEditTarget(undefined);
              setModalMode("add");
            }}
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white rounded-lg cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90 shrink-0 ml-4"
            style={{ backgroundColor: "#830051" }}
          >
            <i className="ri-add-line text-base"></i>
            {t("docCategories.newCategory")}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 rounded-lg border border-red-200 bg-red-50 flex items-center gap-2">
            <i className="ri-error-warning-line text-red-500 text-sm"></i>
            <span className="text-xs text-red-700">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-xs text-red-500 hover:text-red-700 cursor-pointer"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        )}

        {/* Search */}
        <div className="mb-4 flex items-center gap-2">
          <AdminSearchForm
            value={searchInput}
            onChange={setSearchInput}
            onSearch={() => setSearchQuery(searchInput)}
            searchLabel={t("common.search")}
            placeholder={t("docCategories.searchPlaceholder")}
            showReset={searchQuery !== "" || searchInput !== ""}
            onReset={() => {
              setSearchQuery("");
              setSearchInput("");
            }}
            resetLabel={t("common.reset")}
          />
          <div className="flex-1" />
          <span className="text-xs text-gray-400 whitespace-nowrap ml-1">
            {t("docCategories.count", { count: filtered.length })}
          </span>
        </div>

        {loading ? (
          <div className="px-5 py-8 text-center">
            <div className="w-6 h-6 border-2 border-gray-200 border-t-[#830051] rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-xs text-gray-400">{t("common.loading")}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-gray-100 rounded-xl overflow-hidden px-5 py-8 text-center">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-2">
              <i className="ri-folder-line text-lg text-gray-400"></i>
            </div>
            <p className="text-sm text-gray-500">
              {searchQuery
                ? t("docCategories.empty.noMatching")
                : t("docCategories.empty.noCategories")}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              {searchQuery
                ? t("docCategories.empty.adjustSearch")
                : t("docCategories.empty.clickNew")}
            </p>
          </div>
        ) : (
          <>
            {/* Custom Categories Section */}
            {filtered.some((c) => c.is_editable) && (
              <div className="border border-gray-100 rounded-xl overflow-hidden mb-4">
                <div
                  className="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-100"
                  style={{ backgroundColor: "#fafafa" }}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-folder-line text-gray-400 text-base"></i>
                  </div>
                  <span className="text-sm font-semibold text-gray-800">
                    {t("docCategories.customCategories")}
                  </span>
                  <span
                    className="text-xs font-semibold px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: "rgba(131,0,81,0.1)", color: "#830051" }}
                  >
                    {filtered.filter((c) => c.is_editable).length}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <div
                    role="row"
                    className="grid min-w-[940px] grid-cols-[180px_minmax(280px,1fr)_136px_136px_104px] gap-4 border-b border-gray-100 bg-gray-50 px-5 py-3"
                  >
                    <div role="columnheader" className="text-xs font-semibold text-gray-500">
                      {t("docCategories.table.name")}
                    </div>
                    <div role="columnheader" className="text-xs font-semibold text-gray-500">
                      {t("docCategories.table.prompt")}
                    </div>
                    <div
                      role="columnheader"
                      className="text-center text-xs font-semibold text-gray-500"
                    >
                      {t("docCategories.visibleToAllUsers")}
                    </div>
                    <div
                      role="columnheader"
                      className="text-center text-xs font-semibold text-gray-500"
                    >
                      {t("docCategories.defaultSelected")}
                    </div>
                    <div
                      role="columnheader"
                      className="text-right text-xs font-semibold text-gray-500"
                    >
                      {t("docCategories.table.action")}
                    </div>
                  </div>
                  <div className="divide-y divide-gray-50 bg-white">
                    {filtered
                      .filter((c) => c.is_editable)
                      .map((cat) => (
                        <div
                          key={cat.id}
                          role="row"
                          className="group grid min-w-[940px] grid-cols-[180px_minmax(280px,1fr)_136px_136px_104px] items-start gap-4 px-5 py-3 transition-colors hover:bg-gray-50/60"
                        >
                          <div className="pt-0.5">
                            <span className="text-sm font-medium text-gray-800">{cat.name}</span>
                          </div>
                          <div className="min-w-0 pt-0.5">
                            <p className="text-xs leading-relaxed line-clamp-2">
                              {cat.prompt ? (
                                <span className="text-gray-500">{cat.prompt}</span>
                              ) : (
                                <span className="text-gray-300 italic">
                                  {t("docCategories.empty.noPromptDefined")}
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="flex justify-center pt-0.5">
                            <CategorySettingStatus
                              enabled={cat.is_visible_to_all_users ?? false}
                              label={`${cat.name}: ${t("docCategories.visibleToAllUsers")}`}
                              enabledLabel={t("docCategories.status.enabled")}
                              disabledLabel={t("docCategories.status.disabled")}
                              updating={
                                updatingSettings[`${cat.id}:is_visible_to_all_users`] ?? false
                              }
                              onToggle={() => handleToggleSetting(cat, "is_visible_to_all_users")}
                            />
                          </div>
                          <div className="flex justify-center pt-0.5">
                            <CategorySettingStatus
                              enabled={cat.is_default_selected ?? false}
                              label={`${cat.name}: ${t("docCategories.defaultSelected")}`}
                              enabledLabel={t("docCategories.status.enabled")}
                              disabledLabel={t("docCategories.status.disabled")}
                              updating={updatingSettings[`${cat.id}:is_default_selected`] ?? false}
                              onToggle={() => handleToggleSetting(cat, "is_default_selected")}
                            />
                          </div>
                          <div className="sticky right-0 z-50 flex items-center gap-1 border-l border-gray-100 bg-white px-1 pt-0.5 group-hover:bg-gray-50">
                            <button
                              onClick={() => {
                                setEditTarget(cat);
                                setModalMode("edit");
                              }}
                              className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer transition-colors"
                              title={t("common.edit")}
                            >
                              <i className="ri-pencil-line text-sm text-gray-400"></i>
                            </button>
                            {cat.is_deletable && (
                              <button
                                onClick={() => setDeleteTarget(cat)}
                                className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-50 cursor-pointer transition-colors"
                                title={t("common.delete")}
                              >
                                <i className="ri-delete-bin-line text-sm text-red-400"></i>
                              </button>
                            )}
                            {!cat.is_deletable && (
                              <div className="w-7 h-7 flex items-center justify-center">
                                <i
                                  className="ri-lock-line text-sm text-gray-300"
                                  title={t("docCategories.modal.builtInNotice")}
                                ></i>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}

            {/* System Categories Section */}
            {filtered.some((c) => !c.is_editable) && (
              <div className="border border-gray-100 rounded-xl overflow-hidden">
                <div
                  className="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-100"
                  style={{ backgroundColor: "#fafafa" }}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-lock-line text-gray-500 text-base"></i>
                  </div>
                  <span className="text-sm font-semibold text-gray-800">
                    {t("docCategories.systemCategories")}
                  </span>
                  <span className="relative group cursor-help">
                    <i className="ri-question-line text-gray-400 text-sm w-4 h-4 flex items-center justify-center"></i>
                    <span className="absolute top-full mt-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block w-64 text-[11px] text-gray-600 bg-white border border-gray-200 rounded-md shadow-sm px-2.5 py-1.5 z-50 leading-relaxed">
                      {t("docCategories.systemCategoriesTooltip")}
                    </span>
                  </span>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                    {filtered.filter((c) => !c.is_editable).length}
                  </span>
                </div>
                <div className="divide-y divide-gray-50 bg-white">
                  {filtered
                    .filter((c) => !c.is_editable)
                    .map((cat) => (
                      <div
                        key={cat.id}
                        className="group flex items-start gap-4 px-5 py-3 transition-colors border-l-gray-300 hover:bg-gray-100/60"
                      >
                        <div className="flex-none w-[180px] pt-0.5">
                          <span className="text-sm font-medium text-gray-500">{cat.name}</span>
                        </div>
                        <div className="flex-1 min-w-[400px] pt-0.5">
                          <p className="text-xs leading-relaxed line-clamp-2">
                            <span className="text-gray-300 italic">
                              {t("docCategories.empty.noPromptNeeded")}
                            </span>
                          </p>
                        </div>
                        <div className="flex-none w-[104px] flex items-center gap-1 sticky right-0 z-50 pt-0.5 bg-inherit group-hover:bg-gray-100/60 px-1 border-l border-gray-100">
                          <div
                            className="w-7 h-7 flex items-center justify-center rounded-md"
                            title={t("docCategories.modal.builtInNotice")}
                          >
                            <i className="ri-lock-line text-sm text-gray-400"></i>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Category Modal */}
        {modalMode && (
          <CategoryModal
            mode={modalMode}
            category={editTarget}
            onSave={handleSave}
            onCancel={() => {
              setModalMode(null);
              setEditTarget(undefined);
            }}
          />
        )}

        {/* Delete Confirm */}
        {deleteTarget && (
          <ConfirmDialog
            title={t("docCategories.modal.deleteTitle")}
            description={t("docCategories.modal.deleteDesc", { name: deleteTarget.name })}
            confirmLabel={t("common.delete")}
            confirmDanger
            onConfirm={handleDelete}
            onCancel={() => setDeleteTarget(null)}
          />
        )}
      </div>
    </AdminLayout>
  );
}
